import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Settings as SettingsIcon,
  User,
  Bell,
  Shield,
  Save,
  Loader2,
  LogOut,
} from 'lucide-react';
import { toast } from 'sonner';

export const Settings = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    notifications_enabled: true,
    email_alerts: true,
  });

  const handleSave = async () => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 500));
    toast.success('Configuración guardada');
    setLoading(false);
  };

  return (
    <div className="space-y-8 max-w-3xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-muted-foreground" />
          Ajustes
        </h1>
        <p className="text-muted-foreground mt-1">
          Gestiona tu cuenta y preferencias
        </p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Perfil
          </CardTitle>
          <CardDescription>
            Tu información de cuenta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={user?.name || ''} disabled className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="bg-muted/50" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Código de Referido</Label>
            <Input 
              value={user?.referral_code || `PS${user?.id?.slice(-6).toUpperCase() || 'TEST0000'}`} 
              disabled 
              className="bg-muted/50 font-mono" 
            />
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notificaciones
          </CardTitle>
          <CardDescription>
            Configura cómo recibir alertas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Notificaciones activadas</p>
              <p className="text-sm text-muted-foreground">Recibir alertas del sistema</p>
            </div>
            <Switch 
              checked={settings.notifications_enabled}
              onCheckedChange={(checked) => setSettings(s => ({ ...s, notifications_enabled: checked }))}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Alertas por email</p>
              <p className="text-sm text-muted-foreground">Recibir resúmenes diarios</p>
            </div>
            <Switch 
              checked={settings.email_alerts}
              onCheckedChange={(checked) => setSettings(s => ({ ...s, email_alerts: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Seguridad
          </CardTitle>
          <CardDescription>
            Opciones de seguridad de tu cuenta
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Cerrar sesión</p>
              <p className="text-sm text-muted-foreground">Cerrar sesión en este dispositivo</p>
            </div>
            <Button variant="destructive" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading}>
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Cambios
        </Button>
      </div>
    </div>
  );
};

export default Settings;
